import { type ZoomLevel, ZoomMode } from "@embedpdf/plugin-zoom/vue";
import { pick, sortBy } from "lodash";
import { defineStore } from "pinia";
import { type LetterFileTagDto } from "../dto/letter-file-tag.dto";
import { type SignatureObjectDto, type WriteSignatureObjectDto } from "../dto/signature.dto";
import {
  LetterFileColor,
  MailingFrequency,
  type UserPreferencesDto,
  type WriteUserPreferencesDto,
} from "../dto/user-preferences.dto";
import { useUserPreferencesApi } from "../http/user-preferences.api";
import { NotificationType } from "../types";

export interface UserPreferencesStoreState {
  mailingFrequency: MailingFrequency;
  onboardingSeen: boolean;
  hideLetterFilesBefore?: string;
  signatureObjects: SignatureObjectDto[];
  color: LetterFileColor;
  job: string;
  city: string;
  enabledMailTypes: NotificationType[];
  lastSeenVersion: number;
  tags: LetterFileTagDto[];
  maxActivatedTags: number;
  prepoWidth: number | undefined;
  prepoHeight: number | undefined;
  absentFrom: string | undefined;
  absentUntil: string | undefined;
  zoomLevel: ZoomLevel;
}

const api = useUserPreferencesApi();
export const useUserPreferencesStore = defineStore("userPreferences", {
  state: (): UserPreferencesStoreState => ({
    mailingFrequency: MailingFrequency.REAL_TIME,
    onboardingSeen: true,
    signatureObjects: [],
    color: LetterFileColor.GREEN,
    job: "",
    city: "",
    enabledMailTypes: Object.values(NotificationType),
    lastSeenVersion: 1,
    tags: [],
    maxActivatedTags: 0,
    hideLetterFilesBefore: "",
    prepoWidth: 0,
    prepoHeight: 0,
    absentFrom: undefined,
    absentUntil: undefined,
    zoomLevel: ZoomMode.FitWidth,
  }),
  getters: {
    defaultSignature(state: UserPreferencesStoreState): SignatureObjectDto | undefined {
      return state.signatureObjects.find(signature => signature.default);
    },
  },
  actions: {
    setPreferences(userPrefs: Omit<UserPreferencesDto, "loaded" | "maxActivatedTags">) {
      if (userPrefs.tags.length > this.maxActivatedTags) {
        throw new Error(`Le maximum de badges activables est de ${this.maxActivatedTags}`);
      }
      this.mailingFrequency = userPrefs.mailingFrequency;
      this.onboardingSeen = userPrefs.onboardingSeen;
      this.hideLetterFilesBefore = userPrefs.hideLetterFilesBefore;
      this.setSignatureObjects(userPrefs.signatureObjects);
      this.color = userPrefs.color;
      this.job = userPrefs.job;
      this.city = userPrefs.city;
      this.enabledMailTypes = userPrefs.enabledMailTypes;
      this.lastSeenVersion = userPrefs.lastSeenVersion;
      this.tags = userPrefs.tags;
      this.prepoWidth = userPrefs.prepoWidth ?? undefined;
      this.prepoHeight = userPrefs.prepoHeight ?? undefined;
      this.absentFrom = userPrefs.absentFrom ?? undefined;
      this.absentUntil = userPrefs.absentUntil ?? undefined;
      this.zoomLevel = userPrefs.zoomLevel ?? ZoomMode.FitWidth;
    },
    setSignatureObjects(signatureObjects: SignatureObjectDto[]) {
      this.signatureObjects = sortBy(signatureObjects, "position");
    },
    async getMine() {
      this.setPreferences(await api.getMine());
    },
    async updateMine(body: WriteUserPreferencesDto) {
      this.setPreferences(await api.updateMine(body));
    },
    async updateSignObjects(body: WriteSignatureObjectDto[]) {
      this.setSignatureObjects(
        await api.updateSignObjects(body.map(item => pick(item, "id", "fileId", "default") as WriteSignatureObjectDto))
      );
    },
    async getSignatureLink(id: number) {
      return api.getSignatureLink(id);
    },
    async setMaxActivatedTags(maxActivatedTags: number) {
      this.maxActivatedTags = maxActivatedTags;
    },
    getSignaturesDimensions(user: string): Promise<any> {
      return api.getSignaturesDimension(user);
    },
  },
});
